import { SeEventDetailsWidgetComponent } from "../event/se.event.details.widget.component";
import { Type } from "@angular/core";
import type { LayoutSecurityFlag } from "../se.org.security.store";
import { LayoutWidget } from "./se.layout.widget.abstract";
import { Merge } from "../../../pojo/Util";
import { SeEventDetailsOccurrencesWidgetComponent } from "../event/se.event.details.occurrences.widget.component";
import { SeEventDetailsRegistrationConfigWidgetComponent } from "../event/se.event.details.registration.config.widget.component";
import { SeEventDetailsAttendanceWidgetComponent } from "../event/se.event.details.attendance.widget.component";
import { SeEventDetailsSurveyConfigWidgetComponent } from "../event/se.event.details.survey.config.widget.component";
import { SeEventDetailsHostOrgWidgetComponent } from "../event/se.event.details.host.org.widget.component";
import { S25Util } from "../../../util/s25-util";
import { S25Number } from "../../../util/core/s25.number";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { SeEventDetailsAttributesWidgetComponent } from "../event/se.event.details.attributes.widget.component";
import { SeEventDetailsContactRolesWidgetComponent } from "../event/se.event.details.contact.roles.widget.component";
import { SeEventEditFormsWidgetComponent } from "../event/se.event.edit.forms.widget.component";
import { SeOrgAboutWidgetComponent } from "../organization/widgets/se.org.about.widget.component";
import { SeOrgPrimaryContactWidgetComponent } from "../organization/widgets/se.org.primary.contact.widget.component";
import { SeOrgFormLinkWidgetComponent } from "../organization/widgets/se.org.form.link.widget.component";
import { SeOrgSocialLinksWidgetComponent } from "../organization/widgets/se.org.social.links.widget.component";
import { SeOrgCustomAttributesWidgetComponent } from "../organization/widgets/se.org.custom.attributes.widget.component";
import { SeOrgCategoriesWidgetComponent } from "../organization/widgets/se.org.categories.widget.component";
import { SeOrgEmbeddedContentWidgetComponent } from "../organization/widgets/se.org.embedded.content.widget.component";
import { SeOrgUpcomingEventsWidgetComponent } from "../organization/widgets/se.org.upcoming.events.widget.component";
import { SeOrgNewsWidgetComponent } from "../organization/widgets/se.org.news.widget.component";
import { SeOrgFilesWidgetComponent } from "../organization/widgets/se.org.files.widget.component";
import { SeOrgMembersWidgetComponent } from "../organization/widgets/se.org.members.widget.component";

export namespace Layouts {
    export const defaultSettingValue: Record<WidgetSettingFieldType, string> = {
        icon: "",
        text: "",
        textarea: "",
        checkbox: "false",
        css: "",
        "dropdown-multiselect": "",
        "attribute-picker": "",
        "single-select": "",
    };
    export function getDefaultSettingsForWidget(meta: WidgetStatic): Record<string, DBSetting> {
        const settings: Record<string, DBSetting> = {};
        for (const [i, setting] of S25Util.array.enumerate(meta.settings ?? [])) {
            Object.assign(settings, getDefaultSettingsForSettingMeta(setting, meta.defaultSettings, i));
        }
        return settings;
    }

    function getDefaultSettingsForSettingMeta(
        meta: WidgetStaticSetting,
        overrides: Record<string, string>,
        order?: number,
    ): Record<string, DBSetting> {
        const setting: DBSetting = {
            id: meta.id,
            type: meta.fieldType ?? meta.type,
            value: overrides[meta.id] ?? defaultSettingValue[meta.fieldType!] ?? "",
            order: order ?? 0,
        };
        const settings: Record<string, DBSetting> = { [meta.id]: setting };
        for (const [i, setting] of S25Util.array.enumerate(meta.settings ?? [])) {
            Object.assign(settings, getDefaultSettingsForSettingMeta(setting, overrides, i));
        }

        return settings;
    }

    export const pages: Record<Page, PageConfig> = {
        "event-details": {
            name: "Event Details",
            widgets: {
                details: SeEventDetailsWidgetComponent,
                occurrences: SeEventDetailsOccurrencesWidgetComponent,
                registrationConfig: SeEventDetailsRegistrationConfigWidgetComponent,
                attendees: SeEventDetailsAttendanceWidgetComponent,
                surveyConfig: SeEventDetailsSurveyConfigWidgetComponent,
                hostedBy: SeEventDetailsHostOrgWidgetComponent,
                attributes: SeEventDetailsAttributesWidgetComponent,
                contactRoles: SeEventDetailsContactRolesWidgetComponent,
                eventEditForms: SeEventEditFormsWidgetComponent,
            },
        },
        "organization-details": {
            name: "Organization Details",
            widgets: {
                about: SeOrgAboutWidgetComponent,
                primaryContact: SeOrgPrimaryContactWidgetComponent,
                formLink: SeOrgFormLinkWidgetComponent,
                socialLinks: SeOrgSocialLinksWidgetComponent,
                customAttributes: SeOrgCustomAttributesWidgetComponent,
                categories: SeOrgCategoriesWidgetComponent,
                embedded: SeOrgEmbeddedContentWidgetComponent,
                upcomingEvents: SeOrgUpcomingEventsWidgetComponent,
                news: SeOrgNewsWidgetComponent,
                files: SeOrgFilesWidgetComponent,
                members: SeOrgMembersWidgetComponent,
            },
        },
    };
    export type WidgetComponent = Type<LayoutWidget<{ META: WidgetStatic }>> & { META: WidgetStatic };
    export type PageConfig = { name: string; widgets: Record<string, WidgetComponent> };

    export type OrgSecurityFlag = LayoutSecurityFlag;

    export const defaultLayouts: { [P in Page]: Layout } = {
        "organization-details": {
            page: "organization-details",
            category: "globalDefaults",
            name: "New Organization Details Layout",
            description: "",
            heading: null,
            sections: [
                {
                    heading: null,
                    headingCss: "",
                    order: 1,
                    visibleToRoles: [],
                    widgets: [
                        {
                            type: "about",
                            order: 1,
                            visibleToRoles: [],
                            settings: Layouts.getDefaultSettingsForWidget(SeOrgAboutWidgetComponent.META),
                        },
                    ],
                },
                {
                    heading: "Primary Contact",
                    headingCss: "",
                    order: 2,
                    visibleToRoles: [],
                    widgets: [
                        {
                            type: "primaryContact",
                            order: 1,
                            visibleToRoles: [],
                            settings: Layouts.getDefaultSettingsForWidget(SeOrgPrimaryContactWidgetComponent.META),
                        },
                    ],
                },
            ],
        },
        "event-details": {
            page: "event-details",
            category: "globalDefaults",
            name: "New Event Details Layout",
            description: "",
            heading: null,
            sections: [
                {
                    heading: null,
                    headingCss: "",
                    order: 1,
                    visibleToRoles: [],
                    widgets: [
                        {
                            type: "details",
                            order: 1,
                            visibleToRoles: [],
                            settings: Layouts.getDefaultSettingsForWidget(SeEventDetailsWidgetComponent.META),
                        },
                    ],
                },
                {
                    heading: "Occurrences",
                    headingCss: "",
                    order: 2,
                    visibleToRoles: [],
                    widgets: [
                        {
                            type: "occurrences",
                            order: 1,
                            visibleToRoles: [],
                            settings: Layouts.getDefaultSettingsForWidget(
                                SeEventDetailsOccurrencesWidgetComponent.META,
                            ),
                        },
                    ],
                },
                {
                    heading: "Registration Configuration",
                    headingCss: "",
                    order: 3,
                    visibleToRoles: [],
                    widgets: [
                        {
                            type: "registrationConfig",
                            order: 1,
                            visibleToRoles: [],
                            settings: Layouts.getDefaultSettingsForWidget(
                                SeEventDetailsRegistrationConfigWidgetComponent.META,
                            ),
                        },
                    ],
                },
                {
                    heading: "Attendees",
                    headingCss: "",
                    order: 4,
                    visibleToRoles: [],
                    widgets: [
                        {
                            type: "attendees",
                            order: 1,
                            visibleToRoles: [],
                            settings: Layouts.getDefaultSettingsForWidget(SeEventDetailsAttendanceWidgetComponent.META),
                        },
                    ],
                },
                {
                    heading: "Survey Configuration",
                    headingCss: "",
                    order: 5,
                    visibleToRoles: [],
                    widgets: [
                        {
                            type: "surveyConfig",
                            order: 1,
                            visibleToRoles: [],
                            settings: Layouts.getDefaultSettingsForWidget(
                                SeEventDetailsSurveyConfigWidgetComponent.META,
                            ),
                        },
                    ],
                },
                {
                    heading: "Edit Event Forms",
                    headingCss: "",
                    order: 6,
                    visibleToRoles: [],
                    widgets: [
                        {
                            type: "eventEditForms",
                            order: 1,
                            visibleToRoles: [],
                            settings: Layouts.getDefaultSettingsForWidget(SeEventEditFormsWidgetComponent.META),
                        },
                    ],
                },
            ],
        },
    };

    export const NAME_MAX_LENGTH = 128;
    export const DESCRIPTION_MAX_LENGTH = 4000;
    export const SECTION_HEADING_MAX_LENGTH = 128;
    export const SETTING_VALUE_MAX_LENGTH = 4000;

    export type Page = "event-details" | "organization-details";

    export type Category = "specificOrg" | "globalDefaults" | "unknown";

    export function pageLabel(page: string): string {
        if (page in pages) {
            return pages[<Page>page].name;
        }
        return page;
    }

    /** Org (or unknown) layouts that override the default for their {@link Page}. */
    export function isPageOverride(layout: { category: Category }): boolean {
        return layout.category !== "globalDefaults";
    }

    /** One global default per page; when duplicates exist, the highest id wins (the server keeps the same one). */
    export function newestDefaultPerPage<T extends { id?: number; category: Category; page: Page }>(layouts: T[]): T[] {
        const pageToDefault = new Map<Page, T>();
        for (const layout of layouts) {
            if (isPageOverride(layout)) continue;
            const current = pageToDefault.get(layout.page);
            const currentId = S25Number.parseInt(current?.id);
            if (!current || !Number.isFinite(currentId) || S25Number.parseInt(layout.id) > currentId) {
                pageToDefault.set(layout.page, layout);
            }
        }
        return [...pageToDefault.values()];
    }

    /** True when the layout is tied to an organization (ids may arrive as numeric strings). */
    export function hasOrgId(orgId?: number | string | null): boolean {
        if (orgId == null || orgId === "") return false;
        return Number.isFinite(Number(orgId));
    }

    /** Compare org ids from mixed JSON/input types (`98` vs `"98"`). */
    export function sameOrgId(left?: number | string | null, right?: number | string | null): boolean {
        return Layouts.hasOrgId(left) && Layouts.hasOrgId(right) && Number(left) === Number(right);
    }

    /** Category to persist: org-scoped when an org id is present, otherwise the global default. */
    export function categoryFromOrgId(orgId?: number | string | null): Category {
        if (Layouts.hasOrgId(orgId)) return "specificOrg";
        return "globalDefaults";
    }

    export function resetToDefaultConfirmMessage(count = 1): string {
        if (count === 1) {
            return "Reset this layout to the global default for its page? Custom configuration will be removed.";
        }
        return `Reset ${count} selected layouts to the global default for each layout's page? Custom configuration will be removed.`;
    }

    export type LayoutListItem = {
        id: number;
        category: Layouts.Category;
        page: Layouts.Page;
        orgId?: number;
        name?: string;
        description?: string;
    };

    export type DBLayout = {
        id?: number;
        category: Layouts.Category;
        page: Layouts.Page;
        orgId?: number;
        name?: string;
        description?: string;
        heading: string | null;
        sections: DBSection[];
        // TODO: add option for CSS
    };

    /** Sections lay out their widgets in one column or two side by side. */
    export const MIN_SECTION_COLUMNS = 1;
    export const MAX_SECTION_COLUMNS = 2;

    export type DBSection = {
        id?: number;
        order?: number;
        heading: string | null;
        widgets: DBWidget[];
        visibleToRoles: number[];
        headingCss?: string;
        columns?: number;
        // TODO: add options for icon
    };

    export type DBWidget = {
        id?: number;
        type: string;
        order?: number;
        visibleToRoles: number[];
        settings: DBSetting[];
    };

    export type DBSetting = {
        id: string;
        type: string;
        value: string;
        order: number;
    };

    export type Layout = Merge<DBLayout, { sections: Section[] }>;
    export type Section = Merge<DBSection, { widgets: Widget[] }>;
    export type Widget = Merge<DBWidget, { settings: Record<string, Setting> }>;
    export type Setting = DBSetting;

    export interface WidgetStatic {
        name: string;
        info?: string;
        settings: WidgetStaticSetting[];
        defaultSettings: Record<string, string>;
        flsFloor?: OrgSecurityFlag;
    }

    export type WidgetStaticSetting = {
        id: string;
        type: WidgetSettingType;
        fieldType?: Layouts.WidgetSettingFieldType;
        label?: string;
        info?: string;
        options?: {
            cssProps?: readonly string[];
            dropdownOptions?: DropDownItem<string>[];
            attributeTypes?: readonly string[];
        };
        settings?: WidgetStaticSetting[];
    };

    export type WidgetSettingType = "sortable-collapses" | "fields" | "field";
    export type WidgetSettingFieldType =
        | "icon"
        | "text"
        | "textarea"
        | "checkbox"
        | "css"
        | "dropdown-multiselect"
        | "attribute-picker"
        | "single-select";

    export type MapSettings<Meta extends Layouts.WidgetStaticSetting> = {
        [K in keyof Meta]: K extends "settings" ? MapSettings<NonNullable<Meta["settings"]>[number]>[] : Meta[K];
    } & { value: Layouts.Setting };

    export function mapSettings<Meta extends Layouts.WidgetStaticSetting, Settings extends Layouts.Widget["settings"]>(
        meta: Meta,
        settings: Settings,
    ): MapSettings<Meta> {
        const setting = settings[meta.id];
        const mapped = <MapSettings<Meta>>{
            ...meta,
            value: {
                ...setting,
                type: setting?.type ?? meta.type,
                value: setting?.value ?? "",
                order: setting?.order ?? 0,
            },
            settings: meta.settings?.map((setting) => mapSettings(setting, settings)),
        };
        if (meta.type === "sortable-collapses") {
            mapped.settings?.sort((a, b) => a.value.order - b.value.order);
        }

        return mapped;
    }
}
